import React, { useEffect, useState } from 'react';
import { jobCardsAPI } from '../services/api';
import { History } from 'lucide-react';

// Day-by-day record of what each operator did on a job card.
//
// The work log has held one row per operator, per day since the logging rework,
// so this is the same data the operator sees on their own screen - surfaced for
// coordinators, supervisors and the owner, who previously saw only the running
// total and had no way to tell who cut what, or when.
const OP_LABEL = { cutting: 'Cutting', pressing: 'Pressing', boring: 'Boring' };
const OP_TONE = {
  cutting: 'text-blue-700 bg-blue-50',
  pressing: 'text-amber-700 bg-amber-50',
  boring: 'text-purple-700 bg-purple-50',
};

export default function WorkLogHistory({ jobCardId, operations = ['cutting', 'boring'], className = '' }) {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!jobCardId) { setLogs([]); return; }
    setLogs(null);
    jobCardsAPI.getWorkLogs(jobCardId)
      .then((d) => { if (!cancelled) setLogs(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setLogs([]); });
    return () => { cancelled = true; };
  }, [jobCardId]);

  if (logs === null) {
    return <p className={`text-xs text-gray-400 ${className}`}>Loading work history…</p>;
  }

  const rows = logs
    .filter((l) => operations.includes(l.operation_type))
    .filter((l) => (l.delta_qty != null ? l.delta_qty : l.quantity) > 0)
    .sort((a, b) => (b.work_day || '').localeCompare(a.work_day || ''));

  return (
    <div className={`border rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b">
        <History className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">Daily Work History</span>
        <span className="text-xs text-gray-400">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-400 px-3 py-3">
          No day-by-day entries recorded yet for this job card.
        </p>
      ) : (
        <div className="divide-y">
          {rows.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex items-center gap-2">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${OP_TONE[l.operation_type] || 'bg-gray-100 text-gray-600'}`}>
                  {OP_LABEL[l.operation_type] || l.operation_type}
                </span>
                <span className="text-gray-700 truncate">
                  {l.work_day
                    ? new Date(l.work_day + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                    : '—'}
                </span>
                <span className="text-gray-400 truncate">{l.operator_name || 'unknown'}</span>
              </div>
              <div className="flex-shrink-0 text-right">
                <span className="font-semibold text-gray-800">
                  +{l.delta_qty != null ? l.delta_qty : l.quantity}
                </span>
                {l.machine_name && (
                  <span className="text-[11px] text-gray-400 ml-2">{l.machine_name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
